<template>
  <div id="wall-sidebar" class="flex flex-col bg-slate-middle overflow-hidden">
    <div ref="sidebar" class="text-slate-white text-sm flex justify-between m-3 flex-wrap">
      <div class="flex mt-1 w-full justify-between flex-wrap">
        <p v-if="props.selectedBoard" class="board-name text-xl font-bold my-2 mr-2 flex items-center">
          {{ props.selectedBoard.name }}
          <img
            v-if="wallStore.getters.selectedWall.value?.boards?.[0]?._id === props.selectedBoard._id"
            :src="TalkBubbleIcon"
            class="w-4 h-4 invert ml-2"
            alt="Felles"
          />
        </p>

        <div class="flex my-2">
          <div
            v-if="!state.searchMenuIsVisible && restrictedActionAllowed && !props.locked"
            class="p-2 rounded-full mr-2 w-9 h-9 flex justify-center items-center cursor-pointer bg-tw-orange-bright"
            tabindex="0"
            @pointerdown="toggleNewNoteMenu()"
            @keydown.enter="toggleNewNoteMenu()"
          >
            <img :src="PlusIcon" class="w-4" :alt="t('aria-new-note')" />
          </div>
          <div
            v-if="state.filterMenuIsVisible && !state.searchMenuIsVisible"
            id="filter-menu"
            class="xl:w-1/4 md:w-1/3 w-full mt-11 fadein border-blue-bottom absolute bg-slate-middle py-3 z-50"
          >
            <div
              v-for="(f, index) in Object.keys(filter.getters.filterCollection.value)"
              :key="index"
              tabindex="0"
              class="text px-5 py-1 my-1 hover:bg-cyan-500 text-left w-full cursor-pointer"
              :class="filterClass(filter.getters.filterCollection.value[f].name)"
              @pointerdown="selectFilter(filter.getters.filterCollection.value[f])"
              @keydown.enter="selectFilter(filter.getters.filterCollection.value[f])"
            >
              {{ t(f) }}
            </div>
          </div>
          <div
            v-if="!state.searchMenuIsVisible"
            class="gray-btn p-2 rounded-full mr-2 w-9 h-9 flex justify-center items-center"
            tabindex="0"
            @pointerup="toggleFilterMenu()"
            @keydown.enter="toggleFilterMenu()"
          >
            <img :src="FilterIcon" class="cursor-pointer w-4 invert" :alt="t('aria-filter')" />
          </div>
          <div
            class="bg-slate-bright p-2 rounded-full w-9 h-9 flex justify-center items-center"
            tabindex="0"
            @pointerup="toggleSearchMenu()"
            @keydown.enter="toggleSearchMenu()"
          >
            <img :src="SearchIcon" class="cursor-pointer w-4 invert" :alt="t('aria-search')" />
          </div>
        </div>
        <div
          v-if="state.searchMenuIsVisible"
          id="search-menu"
          class="absolute left-0 w-fit -mt-3 fadein rounded-lg bg-slate-middle py-3 z-50 flex justify-between items-center"
        >
          <input
            ref="searchbar"
            v-model="searchText"
            class="w-3/4 ml-3 rounded-3xl p-1.5 text-black bg-white"
            type="text"
            :placeholder="t('search')"
            @keyup="searchNotes"
          />
          <div class="bg-slate-dark p-2 rounded-full" tabindex="0" @pointerdown="toggleSearchMenu()" @keydown.enter="toggleSearchMenu()">
            <img type="image" :src="CloseIcon" class="cursor-pointer rotate-45 invert h-4 w-4" />
          </div>
        </div>
      </div>
    </div>
    <div class="h-px bg-slate-bright mx-3"></div>

    <!-- Filtered results -->
    <div
      v-if="props.filterEntries && props.filterEntries.length > 0 && !state.searchMenuIsVisible"
      id="note-wrapper"
      ref="noteWrapper"
      :key="props.render"
      class="overflow-y-scroll overflow-x-hidden text-slate-white text-base pl-1"
      :class="noteWrapperClass"
      @touchmove="manageScrollMove($event)"
    >
      <template v-for="(filteredNote, index) in props.filterEntries" :key="index">
        <div class="flex items-center mx-2 mt-4 mb-2 cursor-pointer">
          <input
            type="image"
            :class="sectionCollapseStyle(index)"
            :src="ArrowDownIcon"
            class="w-3 mr-3"
            :alt="t('aria-expand-section')"
            @mousedown.left="setSectionIsVisible(index)"
            @touchstart.prevent="setSectionIsVisible(index)"
          />
          <div
            v-if="!['time', 'highlights'].some((i) => i == state.selectedFilter.name)"
            class="h-3 w-3 rounded"
            :style="`background-color: ${color.actions.selectColor(filteredNote.id).background};`"
          ></div>
          <p class="ml-2 text-sm font-semibold text-white">{{ filteredNote.id }}</p>
        </div>
        <template v-if="!state.sectionIsVisible[index]">
          <SidebarNote
            v-for="(note, index2) in filterNotes(filteredNote)"
            :key="index2"
            :note="note"
            :wall-id="filter.getters.selectedBoardId.value"
            :locked="props.locked"
            class="text-slate-white duration-500 transition-transform md:m-2.5 mx-3 my-4"
            :class="{
              'mb-7': index2 + 1 === filterNotes(filteredNote).length,
              'scale-105': state.clickedNote?._id === note._id,
            }"
            @pointerdown="pointerDown(note, $event)"
            @pointermove="pointerMove(note, $event)"
            @pointerup="pointerUp($event)"
            @delete-note="deleteNote"
            @edit-action="setPerformingEditAction"
          />
        </template>
      </template>
    </div>

    <!-- Search results -->
    <template v-else-if="state.searchMenuIsVisible && state.searchResults.length > 0">
      <SidebarNote
        v-for="(note, index) in state.searchResults"
        class="text-slate-white duration-500 transition-transform md:m-2.5 mx-3 my-4"
        :key="index"
        :note="note"
        :wall-id="filter.getters.selectedBoardId.value"
        :locked="props.locked"
        :class="{
          'mb-7': index + 1 === state.searchResults.length,
          'scale-105': state.clickedNote?._id === note._id,
        }"
        @pointerdown.prevent="pointerDown(note, $event)"
        @pointermove.prevent="pointerMove(note, $event)"
        @pointerup="pointerUp($event)"
        @delete-note="deleteNote"
        @edit-action="setPerformingEditAction"
      />
    </template>

    <div v-else :key="props.render + 1">
      <p class="text-white text-md mt-5">{{ t('nothingtodisplay') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, reactive, ref, computed, PropType, nextTick } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { AttributeFilter, FilterEntry } from '@/common/interfaces'
  import { Board, Note } from '@/common/classes'
  import { FilterType, ScreensizeType } from '@/constants'

  import useFilterStore from '@/composables/filter'
  import useColorStore from '@/composables/colors'
  import useCommonStore from '@/composables/common'
  import useWallStore from '@/composables/wallStore'

  import SidebarNote from './SidebarNote.vue'

  import PlusIcon from '@/assets/icons/fontawesome/plus-solid.svg'
  import ArrowDownIcon from '@/assets/icons/arrow-open-small-filled-white.svg'
  import SearchIcon from '@/assets/icons/fontawesome/magnifying-glass-solid.svg'
  import CloseIcon from '@/assets/icons/fontawesome/plus-solid.svg'
  import FilterIcon from '@/assets/icons/fontawesome/sliders-regular.svg'
  import TalkBubbleIcon from '@/assets/icons/fontawesome/comment-regular.svg'

  const messages = {
    no: {
      tags: 'Emneknagger',
      users: 'Deltagere',
      boards: 'Tavler',
      highlights: 'Fremhevet',
      time: 'Når lagt til',
      update: 'Når oppdatert',
      actionprohibited: 'Handlingen er ikke mulig, er enten ikke tilltatt eller på grunn av konflikt med annen handling',
      filter: 'Filter',
      board: 'Tavle',
    },
    en: {
      tags: 'Tags',
      users: 'Participants',
      boards: 'Walls',
      highlights: 'Highlights',
      time: 'Date added',
      update: 'Date updated',
      actionprohibited: 'Action not possible due to restsricted access or conflicting actions',
      filter: 'Filter',
      board: 'Wall',
    },
  }

  const searchText = ref('')
  const sidebar = ref<HTMLInputElement | null>(null)
  const searchbar = ref<HTMLInputElement | null>(null)
  const noteWrapper = ref<HTMLInputElement | null>(null)

  const filter = useFilterStore()
  const color = useColorStore()
  const common = useCommonStore()
  const wallStore = useWallStore()

  const emit = defineEmits<{
    (e: 'updateSidebarValue', value: number): void
    (e: 'initiateNoteDrag', note: Note, event: PointerEvent): void
    (e: 'toggleNewNoteMenu'): void
    (e: 'deleteNote', note: Note): void
  }>()

  const props = defineProps({
    notes: { type: Object as PropType<Note[]>, default: () => Array },
    filterEntries: { type: Object as PropType<FilterEntry[]>, default: () => Array },
    selectedBoard: { type: Object as PropType<Board>, required: true },

    dragIsOngoing: { type: Boolean, required: true },

    render: { type: Number, default: 0 },
    restrictedActionAllowed: { type: Boolean, required: true },
    locked: { type: Boolean, default: false },
  })

  const state = reactive({
    searchResults: [] as Note[],
    selectedFilter: {} as AttributeFilter,

    filterMenuIsVisible: false,
    searchMenuIsVisible: false,
    performingEditAction: false,

    sectionIsVisible: {} as Record<string, boolean>,

    clickedNote: undefined as Note | undefined,
    previousMousePosition: { x: 0, y: 0 },
  })

  const { t } = useI18n({ messages })
  let holdTimer: NodeJS.Timeout

  onMounted(() => {
    filter.actions.setFilterType(FilterType.TIME) // time is the default filter
    state.selectedFilter = filter.getters.filterCollection.value[FilterType.TIME]
    if (sidebar.value) emit('updateSidebarValue', sidebar.value.clientWidth)
  })

  const noteWrapperClass = computed(() => {
    return props.dragIsOngoing && !state.performingEditAction ? 'opacity-50' : ''
  })

  function filterNotes(entry: FilterEntry) {
    const filteredNotes = props.notes.filter((c) => Array.from(entry.noteIds).some((id) => c._id === id))
    filter.actions.setFilteredNotes(filteredNotes)
    return filteredNotes
  }

  function toggleSearchMenu() {
    state.searchMenuIsVisible = !state.searchMenuIsVisible
    if (state.searchMenuIsVisible) {
      searchText.value = ''
      state.searchResults = filter.getters.filteredNotes.value
      nextTick().then(() => {
        searchbar.value?.focus()
      })
    } else state.searchResults = props.notes // reset to initial value
    state.filterMenuIsVisible = false
  }

  function toggleFilterMenu() {
    state.filterMenuIsVisible = !state.filterMenuIsVisible
    state.searchMenuIsVisible = false
  }

  function toggleNewNoteMenu() {
    emit('toggleNewNoteMenu')
  }

  function selectFilter(f: AttributeFilter) {
    state.sectionIsVisible = {} // reset section collapse
    state.selectedFilter = f

    filter.actions.resetFilteredNotes()
    filter.actions.setFilterType(f.name)
    toggleFilterMenu()
  }

  // If we are dragging a note to the board, we need to prevent scroll and iOS "bounce" activity with the touchmove event
  function manageScrollMove(event: TouchEvent) {
    if (state.clickedNote || props.dragIsOngoing) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }

  function pointerDown(note: Note, event: PointerEvent) {
    const popDelay = event.pointerType === 'mouse' ? 300 : 700
    const dragAllowed =
      common.getters.screensizeType.value !== ScreensizeType.phone &&
      (note.entries === undefined || note.entries[props.selectedBoard._id || ''] === undefined) &&
      !state.performingEditAction &&
      props.restrictedActionAllowed
    // prevent other mouse actions from triggering the drag feature when a note is already pinned...
    if (dragAllowed) {
      state.clickedNote = note
      state.previousMousePosition = { x: event.clientX, y: event.clientY }
      holdTimer = setTimeout(() => {
        if (navigator.vibrate) {
          navigator.vibrate(50) // 50ms vibration
        }
        state.clickedNote = undefined
        emit('initiateNoteDrag', note, event)
      }, popDelay) // 500ms delay
    }
  }

  function pointerMove(note: Note, event: PointerEvent) {
    const { diffX, diffY } = {
      diffX: Math.abs(event.clientX - state.previousMousePosition.x),
      diffY: Math.abs(event.clientY - state.previousMousePosition.y),
    }
    const overThreshold = diffX > 10 || diffY > 10
    if (!props.dragIsOngoing && state.clickedNote && overThreshold) {
      state.clickedNote = undefined
      clearTimeout(holdTimer)
    } else if (state.clickedNote) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }

  function pointerUp(event: PointerEvent) {
    clearTimeout(holdTimer)
    state.clickedNote = undefined
  }

  function searchNotes() {
    state.searchResults = []
    let search = searchText.value.toLowerCase()
    if (search.length === 0) {
      state.searchResults = filter.getters.filteredNotes.value
    } else {
      for (let note of filter.getters.filteredNotes.value) {
        if (note.text.toLowerCase().includes(search) || note.status.createdBy.username.toLowerCase().includes(search)) {
          state.searchResults.push(note)
        }
      }
    }
  }

  function filterClass(name: string) {
    return name === state.selectedFilter.name ? 'bg-cyan-500' : ''
  }

  function setSectionIsVisible(index: number) {
    let flag = state.sectionIsVisible[index] !== undefined ? state.sectionIsVisible[index] : false
    return (state.sectionIsVisible[index] = !flag)
  }

  function sectionCollapseStyle(index: number) {
    return !state.sectionIsVisible[index] ? '' : '-rotate-90'
  }

  function setPerformingEditAction(value: boolean) {
    state.performingEditAction = value
  }

  function deleteNote(note: Note) {
    emit('deleteNote', note)
  }
</script>

<style scoped>
  .board-name {
    max-width: 75%;
  }
</style>
